/**
 * Performance optimization utilities for the application
 */

// Typed cache for storing expensive computation results
interface MemoCache<T = any> {
  value: T;
  timestamp: number;
  hitCount: number;
}

// Global cache for storing expensive computation results
const memoizationCache = new Map<string, MemoCache>();

// Cache statistics
const cacheStats = {
  hits: 0,
  misses: 0,
  evictions: 0,
};

export interface MemoizeOptions {
  /**
   * Maximum time in milliseconds to keep cache entries
   * Setting to 0 or less means no expiration
   */
  maxAge?: number;
  
  /**
   * Function to generate a unique cache key from the arguments
   */
  keyFn?: (...args: any[]) => string;
  
  /**
   * Maximum cache size (number of entries) before eviction
   */
  maxSize?: number;
}

/**
 * Memoizes an expensive function result based on its arguments
 * @param fn The function to memoize
 * @param options Memoization options
 * @returns Memoized function that returns cached results for the same inputs
 */
export function memoize<T extends (...args: any[]) => any>(
  fn: T,
  options: MemoizeOptions = {}
): T {
  const {
    maxAge = 0, // No expiration by default
    keyFn = (...args) => JSON.stringify(args),
    maxSize = 1000, // Default to 1000 entries max
  } = options;
  
  return ((...args: Parameters<T>): ReturnType<T> => {
    const cacheKey = keyFn(...args);
    const now = Date.now();
    
    // Check cache hit with optional expiration
    if (memoizationCache.has(cacheKey)) {
      const cached = memoizationCache.get(cacheKey)!;
      
      // Check if cache entry is still valid
      if (maxAge <= 0 || now - cached.timestamp < maxAge) {
        cached.hitCount++;
        cacheStats.hits++;
        return cached.value as ReturnType<T>;
      }
    }
    
    // Cache miss or expired
    cacheStats.misses++;
    
    // Enforce max size with Least Frequently Used (LFU) eviction policy
    if (maxSize > 0 && memoizationCache.size >= maxSize) {
      // Find entry with lowest hit count to evict
      let leastUsedKey: string | null = null;
      let leastUsedHits = Infinity;
      
      // Convert to array first to avoid iterator issues with different TypeScript targets
      const entries = Array.from(memoizationCache.entries());
      
      for (let i = 0; i < entries.length; i++) {
        const [key, entry] = entries[i];
        if (entry.hitCount < leastUsedHits) {
          leastUsedKey = key;
          leastUsedHits = entry.hitCount;
        }
      }
      
      // Evict least used entry
      if (leastUsedKey) {
        memoizationCache.delete(leastUsedKey);
        cacheStats.evictions++;
      }
    }
    
    // Compute and cache result
    const result = fn(...args);
    memoizationCache.set(cacheKey, {
      value: result,
      timestamp: now,
      hitCount: 1,
    });
    
    return result;
  }) as T;
}

/**
 * Clears the memoization cache
 * @param keyPattern Optional regex pattern to match keys to clear
 * @returns Number of entries cleared
 */
export function clearMemoizationCache(keyPattern?: RegExp): number {
  if (!keyPattern) {
    const size = memoizationCache.size;
    memoizationCache.clear();
    return size;
  }
  
  // Clear only keys that match the pattern
  let cleared = 0;
  Array.from(memoizationCache.keys()).forEach(key => {
    if (keyPattern.test(key)) {
      memoizationCache.delete(key);
      cleared++;
    }
  });
  
  return cleared;
}

/**
 * Gets statistics about the memoization cache
 * @returns Cache statistics
 */
export function getMemoizationStats(): { 
  size: number; 
  hits: number; 
  misses: number; 
  evictions: number;
  hitRatio: number;
} {
  return {
    size: memoizationCache.size,
    hits: cacheStats.hits,
    misses: cacheStats.misses,
    evictions: cacheStats.evictions,
    hitRatio: cacheStats.hits / (cacheStats.hits + cacheStats.misses) || 0,
  };
}

/**
 * Reset memoization statistics
 */
export function resetMemoizationStats(): void {
  cacheStats.hits = 0;
  cacheStats.misses = 0;
  cacheStats.evictions = 0;
}

/**
 * Debounces a function call
 * @param fn Function to debounce
 * @param delay Delay in milliseconds
 * @param options Additional options
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number,
  options: { leading?: boolean; maxWait?: number } = {}
): {
  (...args: Parameters<T>): void;
  cancel: () => void;
  flush: (...args: Parameters<T>) => ReturnType<T> | undefined;
} {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastCallTime = 0;
  let lastArgs: Parameters<T> | null = null;
  let lastResult: ReturnType<T> | undefined;
  const { leading = false, maxWait = 0 } = options;
  
  const debounced = function(this: any, ...args: Parameters<T>): void {
    const time = Date.now();
    lastArgs = args;
    
    // If leading execution is requested and this is the first call
    if (leading && !timeoutId) {
      lastCallTime = time;
      lastResult = fn.apply(this, args);
      return;
    }
    
    // Handle max wait time if specified
    const shouldInvokeNow = maxWait > 0 && time - lastCallTime >= maxWait;
    
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    const later = () => {
      timeoutId = null;
      lastCallTime = Date.now();
      if (lastArgs) {
        lastResult = fn.apply(this, lastArgs);
        lastArgs = null;
      }
    };
    
    if (shouldInvokeNow) {
      timeoutId = null;
      lastCallTime = time;
      lastResult = fn.apply(this, args);
    } else {
      timeoutId = setTimeout(later, delay);
    }
  };
  
  // Additional methods for the debounced function
  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastArgs = null;
  };
  
  debounced.flush = (...args: Parameters<T>): ReturnType<T> | undefined => {
    debounced.cancel();
    lastArgs = args;
    lastResult = fn.apply(null, args);
    return lastResult;
  };
  
  return debounced;
}

/**
 * Throttles a function call
 * @param fn Function to throttle
 * @param limit Time limit in milliseconds
 * @param options Additional options
 * @returns Throttled function with cancel method
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  limit: number,
  options: { leading?: boolean; trailing?: boolean } = {}
): {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel: () => void;
} {
  let lastCall = 0;
  let timeoutId: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastResult: ReturnType<T> | undefined;
  let lastCallTime = 0;
  const { leading = true, trailing = true } = options;
  
  const throttled = function(this: any, ...args: Parameters<T>): ReturnType<T> | undefined {
    const now = Date.now();
    lastArgs = args;
    
    // First call, or enough time has passed since last call
    if (!lastCallTime && leading === false) {
      lastCallTime = now;
    }
    
    const remaining = limit - (now - lastCallTime);
    
    // Time has passed or it's the first call
    if (remaining <= 0 || remaining > limit) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      lastCallTime = now;
      lastResult = fn.apply(this, args);
      lastArgs = null;
      
      return lastResult;
    } 
    // Schedule trailing call if requested
    else if (!timeoutId && trailing && lastArgs) {
      timeoutId = setTimeout(() => {
        lastCallTime = leading ? Date.now() : 0;
        timeoutId = null;
        
        if (lastArgs) {
          lastResult = fn.apply(this, lastArgs);
          lastArgs = null;
        }
      }, remaining);
    }
    
    return lastResult;
  };
  
  throttled.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastCallTime = 0;
    lastArgs = null;
  };
  
  return throttled;
}

/**
 * Creates a unique ID for components
 * @returns Unique string ID
 */
export function generateId(prefix: string = ''): string {
  const uniquePart = Math.random().toString(36).substring(2, 15) + 
                     Math.random().toString(36).substring(2, 15);
  return prefix ? `${prefix}-${uniquePart}` : uniquePart;
}

/**
 * Measure performance of a function
 * @param fn Function to measure
 * @param label Label for the performance measurement
 * @returns The same function wrapped with performance measurement
 */
export function measurePerformance<T extends (...args: any[]) => any>(
  fn: T,
  label: string,
  options: { 
    threshold?: number; 
    logLevel?: 'debug' | 'log' | 'warn' | 'error';
    silent?: boolean;
  } = {}
): T {
  const { 
    threshold = 0, 
    logLevel = 'log',
    silent = false,
  } = options;
  
  return ((...args: Parameters<T>): ReturnType<T> => {
    // Start performance measurement
    const start = performance.now();
    
    try {
      const result = fn(...args);
      
      // If result is a promise, measure async performance
      if (result instanceof Promise) {
        return result.finally(() => {
          const end = performance.now();
          const duration = end - start;
          
          if (!silent && (threshold === 0 || duration > threshold)) {
            console[logLevel](`⏱️ ${label} took ${duration.toFixed(2)}ms (async)`);
          }
        }) as ReturnType<T>;
      }
      
      // For synchronous functions
      const end = performance.now();
      const duration = end - start;
      
      if (!silent && (threshold === 0 || duration > threshold)) {
        console[logLevel](`⏱️ ${label} took ${duration.toFixed(2)}ms`);
      }
      
      return result;
    } catch (error) {
      const end = performance.now();
      console.error(`❌ ${label} failed after ${(end - start).toFixed(2)}ms:`, error);
      throw error;
    }
  }) as T;
}

/**
 * Batch multiple operations together to reduce reflows/repaints
 * @param tasks Array of functions to execute in batch
 * @returns Results of all tasks
 */
export function batchOperations<T>(tasks: Array<() => T>): T[] {
  // Request animation frame to batch operations in the next frame
  return new Promise<T[]>(resolve => {
    requestAnimationFrame(() => {
      const results = tasks.map(task => task());
      resolve(results);
    });
  }) as any as T[];
}

/**
 * RAF-based throttle for animations and scroll events
 * @param fn Function to throttle with requestAnimationFrame
 * @returns Throttled function
 */
export function rafThrottle<T extends (...args: any[]) => void>(fn: T): (...args: Parameters<T>) => void {
  let scheduled = false;
  let lastArgs: Parameters<T> | null = null;
  
  return (...args: Parameters<T>): void => {
    lastArgs = args;
    
    if (scheduled) return;
    
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      if (lastArgs) {
        fn(...lastArgs);
      }
    });
  };
}
